import { ModelCardInterface } from "@dashart/dashart-gpt-shared-library";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { ProviderOptionsType } from "../../../app/interfaces/ProviderOptionsInterface";
import ButtonWithIcon from "../buttonWithIcon/ButtonWithIcon";
import { AdvancedOptionsIcon } from "../icons/AdvancedOptionsIcon";
import { BrainIcon } from '../icons/BrainIcon';
import { LinesCountIcon } from "../icons/LinesCountIcon";
import { TemperatureIcon } from '../icons/TemperatureIcon';
import { TokensIcon } from "../icons/TokensIcon";
import ProviderOptions from "../providerOptions/ProviderOptions";
import SelectorDialogDialog from './SelectorDialog/SelectorDialog';


const maxTokensValues = [
    null, 200, 500, 1000, 2000, 4000, 8000, 16000, 32000, 64000
]
const maxTokensListValues = [
    ...maxTokensValues.map((option) => {
        return {
            label: `${option}`,
            value: option,
        }
    })
]
// values between 0.1 and 2.0
const temperatureValues = [
    ...Array.from(Array(20).keys()).map((index) => {
        return (index + 1) / 10 * 1;
    })
]
const temperatureListValues = [
    ...temperatureValues.map((option) => {
        return {
            label: `${option}`,
            value: option,
        }
    })
]

// values between 4 and 20
const inputLinesValues = [
    4, // default
    ...Array.from(Array(8).keys()).map((index) => {
        return (index + 1) * 2 + 4;
    })
]
const inputLinesListValues = [
    ...inputLinesValues.map((option) => {
        return {
            label: `${option}`,
            value: option,
        }
    })
]

const iconDefaultProps = {
    width: 24,
    height: 24,
}

export interface ChatOptionsProps {
    completionIsRunning: boolean;
    completionError: string | null;

    gptModel: ModelCardInterface;
    availableModels: ModelCardInterface[];
    temperature: number;
    maxTokens: number;
    inputTextLines: number;

    providerOptions: ProviderOptionsType;
    onProviderOptionsChange: (options: ProviderOptionsType) => void;

    setGptModel: (model: ModelCardInterface) => void;
    setTemperature: (temperature: number) => void;
    setMaxTokens: (maxTokens: number) => void;
    setInputTextLines: (inputTextLines: number) => void;
}

export const ChatOptions: React.FunctionComponent<ChatOptionsProps> = (props: ChatOptionsProps) => {
    const {
        completionIsRunning,
        completionError,
        gptModel,
        temperature,
        maxTokens,
        inputTextLines,
        providerOptions,
        onProviderOptionsChange,
        setTemperature,
        setMaxTokens,
        setInputTextLines,
    } = props;
    const { t } = useTranslation();
    const [display, setDisplay] = useState({
        model: false,
        temp: false,
        lines: false,
        tokens: false,
        advancedOptions: false
    });
    const [dialogWidth, setDialogWidth] = useState('50vw');

    /**
     * Current state of the completion or the error message
     */
    const completionStateText = useMemo(() => {
        if (completionIsRunning) {
            return t('components.chat_options.status.loading');
        }

        if (completionError) {
            return completionError;
        }
        return t('components.chat_options.status.idle');
    }, [completionIsRunning, completionError])

    const showDialog = (name: string) => {
        setDisplay(prev => ({ ...prev, [name]: true }));
    };

    const hideDialog = (name: string) => {
        setDisplay(prev => ({ ...prev, [name]: false }));
    };

    /**
     * Resize the dialog
     */
    useEffect(() => {
        const handleResize = () => {
            setDialogWidth(window.innerWidth < 1024 ? '95vw' : '50vw');
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    /**
     * Determine the current provider based on the model ID
     */
    const getCurrentProvider = () => {
        if (!gptModel?.id) return '';
        
        if (gptModel.id.startsWith('gpt-') || gptModel.id.startsWith('o1') || 
            gptModel.id.startsWith('o4') || gptModel.id.startsWith('o3')) {
            return 'openai';
        } else if (gptModel.id.startsWith('claude-')) {
            return 'anthropic';
        } else if (gptModel.id.startsWith('mistral-') || gptModel.id.startsWith('mixtral-')) {
            return 'mistral';
        } else if (gptModel.id.startsWith('gemini-')) {
            return 'google';
        } else if (gptModel.id.includes('/')) { // OpenRouter models often include provider names
            return 'openrouter';
        }
        
        return '';
    };

    /**
     * Handle wheel event for temperature
     * @param e - Wheel event
     */
    const handleTemperatureWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const currentIndex = temperatureValues.findIndex(v => v === temperature);
        let newIndex = currentIndex;
        
        if (e.deltaY < 0) { // Scroll up: increase value
            newIndex = Math.min(currentIndex + 1, temperatureValues.length - 1);
        } else { // Scroll down: decrease value
            newIndex = Math.max(currentIndex - 1, 0);
        }
        
        if (newIndex !== currentIndex) {
            setTemperature(temperatureValues[newIndex]);
        }
    };

    /**
     * Handle wheel event for max tokens
     * @param e - Wheel event
     */
    const handleMaxTokensWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const currentIndex = maxTokensValues.findIndex(v => v === maxTokens);
        let newIndex = currentIndex;
        
        if (e.deltaY < 0) { // Scroll up: increase value
            newIndex = Math.min(currentIndex + 1, maxTokensValues.length - 1);
        } else { // Scroll down: decrease value
            newIndex = Math.max(currentIndex - 1, 0);
        }
        
        if (newIndex !== currentIndex) {
            setMaxTokens(maxTokensValues[newIndex]);
        }
    };

    /**
     * Handle wheel event for input lines
     * @param e - Wheel event
     */
    const handleInputLinesWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const currentIndex = inputLinesValues.findIndex(v => v === inputTextLines);
        let newIndex = currentIndex;
        
        if (e.deltaY < 0) { // Scroll up: increase value
            newIndex = Math.min(currentIndex + 1, inputLinesValues.length - 1);
        } else { // Scroll down: decrease value
            newIndex = Math.max(currentIndex - 1, 0);
        }
        
        if (newIndex !== currentIndex) {
            setInputTextLines(inputLinesValues[newIndex]);
        }
    };

    /**
     * Set default values
     */
    React.useEffect(() => {
        // set default values
        setTemperature(temperatureValues[9]); // default is 1.0
        setMaxTokens(maxTokensValues[0]);
    }, []);

    return (
        <div key="1" className={'flex width-full pl-2 pr-2 pt-1 pb-1'}>

            {/* overlays */}
            <>
                {/* model selection */}
                {display.model ? <SelectorDialogDialog
                    header={t('components.chat_options.selected_model.header')}
                    description={t('components.chat_options.selected_model.description')}
                    width={dialogWidth}
                    labelKey="id"
                    availableValues={props.availableModels}
                    selectedValues={props.gptModel}
                    onSelect={(model) => {
                        // take care user does not deselect a model,
                        // we need always a selected model
                        if (model) {
                            props.setGptModel(model);
                        }
                        hideDialog('model');
                    }}
                    onclose={() => hideDialog('model')}
                /> : null}

                {/* temperature selection */}
                {display.temp ? <SelectorDialogDialog
                    header={t('components.chat_options.temperature.header')}
                    description={t('components.chat_options.temperature.description')}
                    width={dialogWidth}
                    labelKey="label"
                    availableValues={temperatureListValues}
                    selectedValues={temperature}
                    onSelect={(value) => {
                        setTemperature(value);
                        hideDialog('temp');
                    }}
                    onclose={() => hideDialog('temp')}
                /> : null}

                {/* lines selection */}
                {display.lines ? <SelectorDialogDialog
                    header={t('components.chat_options.input_lines_count.header')}
                    description={t('components.chat_options.input_lines_count.description')}
                    width={dialogWidth}
                    labelKey="label"
                    availableValues={inputLinesListValues}
                    selectedValues={inputTextLines}
                    onSelect={(value) => {
                        setInputTextLines(value);
                        hideDialog('lines');
                    }}
                    onclose={() => hideDialog('lines')}
                /> : null}

                {/* maximum tokens selection */}
                {display.tokens ? <SelectorDialogDialog
                    header={t('components.chat_options.max_tokens.header')}
                    description={t('components.chat_options.max_tokens.description')}
                    width={dialogWidth}
                    labelKey="label"
                    availableValues={maxTokensListValues}
                    selectedValues={maxTokens}
                    onSelect={(value) => {
                        setMaxTokens(value);
                        hideDialog('tokens');
                    }}
                    onclose={() => hideDialog('tokens')}
                /> : null}

                {/* advanced options dialog */}
                {display.advancedOptions ? (
                    <Dialog
                        header={t('components.chat_options.advanced_options.header')}
                        visible={true}
                        style={{ width: dialogWidth }}
                        onHide={() => hideDialog('advancedOptions')}
                        closeOnEscape={true}
                        dismissableMask={true}
                    >
                        <ProviderOptions
                            provider={getCurrentProvider()}
                            options={providerOptions}
                            onOptionsChange={onProviderOptionsChange}
                        />
                    </Dialog>
                ) : null}
            </>

            <div className={'flex-grow-1 flex align-items-center'}>
                {t('components.chat_options.status.label')}:&nbsp;{completionStateText}
            </div>
            <div className={'flex align-items-center flex-end gap-3'}>
                <ButtonWithIcon
                    icon={
                        <BrainIcon {...iconDefaultProps} />
                    }
                    onClick={() => showDialog('model')}
                    label={gptModel?.id ?? 'null'}
                />

                {props.gptModel?.supportsTemperature ?
                    <div 
                        onWheel={handleTemperatureWheel}
                        title={t('components.chat_options.temperature.tooltip_scroll')}
                        className="wheel-control"
                    >
                        <ButtonWithIcon
                            icon={
                                <TemperatureIcon {...iconDefaultProps} />
                            }
                            onClick={() => showDialog('temp')}
                            label={temperature?.toString() ?? 'null'}
                        />
                    </div> : null}

                <div 
                    onWheel={handleMaxTokensWheel}
                    title={t('components.chat_options.max_tokens.tooltip_scroll')}
                    className="wheel-control"
                >
                    <ButtonWithIcon
                        icon={
                            <TokensIcon {...iconDefaultProps} />
                        } additionalClassNames="p-button-outlined"
                        onClick={() => showDialog('tokens')}
                        label={maxTokens?.toString() ?? 'null'}
                    />
                </div>

                <div 
                    onWheel={handleInputLinesWheel}
                    title={t('components.chat_options.input_lines_count.tooltip_scroll')}
                    className="wheel-control"
                >
                    <ButtonWithIcon
                        icon={
                            <LinesCountIcon {...iconDefaultProps} />
                        } additionalClassNames="p-button-outlined"
                        onClick={() => showDialog('lines')}
                        label={inputTextLines?.toString() ?? 'null'}
                    />
                </div>

                {/* Only show advanced options for Claude/Anthropic provider */}
                {getCurrentProvider() === 'anthropic' && (
                    <ButtonWithIcon
                        icon={
                            <AdvancedOptionsIcon {...iconDefaultProps} />
                        } 
                        additionalClassNames="p-button-outlined p-button-info"
                        onClick={() => showDialog('advancedOptions')}
                        label={t('components.chat_options.advanced_options.button')}
                    />
                )}
            </div>
        </div>
    )
}

export default ChatOptions;
